import { Calendar, DollarSign, Loader2, TrendingUp } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useGetAccountsReceivable } from '~entities/dashboard/api/get-accounts-receivable';
import { useGetCollectedRevenueMonthlyTrend } from '~entities/dashboard/api/get-collected-revenue-monthly-trend';

import {
  getCollectedRevenueMonthlyTrendEndDate,
  getCollectedRevenueMonthlyTrendStartDate,
  getCurrentMonthTrend,
} from '../helpers/collected-revenue-monthly-trend';

import { useGetCurrency } from '~shared/api/currency-api';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { formatCurrencyAmount } from '../helpers/format-currency';

interface RevenueDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
}

export function RevenueDetailModal({
  open,
  onOpenChange,
  sessionId,
}: RevenueDetailModalProps) {
  const { data: currencyDetails } = useGetCurrency();
  const currencySymbol = currencyDetails?.symbol ?? '$';

  const {
    data: collectedRevenueMonthlyTrendData,
    isFetching: isCollectedRevenueMonthlyTrendLoading,
  } = useGetCollectedRevenueMonthlyTrend({
    sessionId,
    startDate: getCollectedRevenueMonthlyTrendStartDate(),
    endDate: getCollectedRevenueMonthlyTrendEndDate(),
  });

  const currentMonthTrend = getCurrentMonthTrend(
    collectedRevenueMonthlyTrendData,
  );

  const {
    data: accountsReceivable,
    isFetching: isAccountsReceivableLoading,
  } = useGetAccountsReceivable({ sessionId });

  const formatCurrency = (value: number) =>
    `${currencySymbol}${formatCurrencyAmount(value)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] max-w-4xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='text-2xl font-semibold text-[#000814]'>
            Revenue Details
          </DialogTitle>
          <DialogDescription>
            Breakdown of collected, outstanding, and scheduled revenue
          </DialogDescription>
        </DialogHeader>

        <div className='mt-6 space-y-6'>
          {/* Summary Stats */}
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
            <div className='rounded-xl border border-[#BBF7D0] bg-[#F0FDF4] p-4 text-[#15803D]'>
              <p className='mb-1 flex items-center gap-1 text-sm'>
                <DollarSign className='size-4' />
                Collected
              </p>
              <p className='text-3xl font-semibold'>
                {isCollectedRevenueMonthlyTrendLoading ? (
                  <Loader2 className='size-8 animate-spin' />
                ) : (
                  formatCurrency(currentMonthTrend?.collected ?? 0)
                )}
              </p>
              <p className='mt-1 text-xs'>This month</p>
            </div>
            <div className='rounded-xl border border-[#FED7AA] bg-[#FFF7ED] p-4 text-[#C2410C]'>
              <p className='mb-1 flex items-center gap-1 text-sm'>
                <Calendar className='size-4' />
                Outstanding
              </p>
              <p className='text-3xl font-semibold'>
                {isAccountsReceivableLoading ? (
                  <Loader2 className='size-8 animate-spin' />
                ) : (
                  formatCurrency(
                    accountsReceivable?.totalAccountReceivable ?? 0,
                  )
                )}
              </p>
              <p className='mt-1 text-xs'>Due this month (matches Accounts Receivable)</p>
            </div>
            <div className='rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] p-4 text-[#1E40AF]'>
              <p className='mb-1 flex items-center gap-1 text-sm'>
                <TrendingUp className='size-4' />
                Scheduled (This Month)
              </p>
              <p className='text-3xl font-semibold'>
                {isCollectedRevenueMonthlyTrendLoading ? (
                  <Loader2 className='size-8 animate-spin' />
                ) : (
                  formatCurrency(currentMonthTrend?.scheduled ?? 0)
                )}
              </p>
              <p className='mt-1 text-xs'>Due today or later this month</p>
            </div>
          </div>

          {/* Monthly Trend */}
          <div>
            <h3 className='mb-3 text-lg font-semibold text-[#000814]'>
              Monthly Trend
            </h3>
            <div className='rounded-xl border border-[#E5E7EB] bg-white p-6'>
              {isCollectedRevenueMonthlyTrendLoading ? (
                <div className='flex h-[300px] items-center justify-center'>
                  <Loader2 className='size-8 animate-spin' />
                </div>
              ) : (
                <ResponsiveContainer width='100%' height={300}>
                  <BarChart data={collectedRevenueMonthlyTrendData}>
                    <CartesianGrid strokeDasharray='3 3' stroke='#E5E7EB' />
                    <XAxis
                      dataKey='month'
                      stroke='#6B7280'
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis
                      stroke='#6B7280'
                      style={{ fontSize: '12px' }}
                      tickFormatter={(value) =>
                        Number(value).toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        })
                      }
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                      }}
                      formatter={(value: number, name: string) => [
                        formatCurrency(value),
                        name,
                      ]}
                    />
                    <Legend iconType='circle' />
                    <Bar
                      dataKey='collected'
                      fill='#16A34A'
                      name='Collected'
                      radius={[8, 8, 0, 0]}
                    />
                    <Bar
                      dataKey='outstanding'
                      fill='#F59E0B'
                      name='Outstanding'
                      radius={[8, 8, 0, 0]}
                    />
                    <Bar
                      dataKey='scheduled'
                      fill='#2563EB'
                      name='Scheduled'
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
